import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Search, FileText, ArrowRight, Clock, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { moduleRegistry } from "src/modules/registry";
import type { LucideIcon } from "lucide-react";

// ============================================================================
// Types
// ============================================================================

interface CommandPaletteEntry {
  id: string;
  label: string;
  type: "page" | "recent";
  path: string;
  icon?: LucideIcon;
  description?: string;
}

interface RecentItem {
  id: string;
  label: string;
  type: string;
  path: string;
  accessedAt: number;
}

// ============================================================================
// Recent Items Persistence
// ============================================================================

const MAX_RECENT = 10;
const RECENT_KEY_PREFIX = "commandPalette:recent:";

function getRecentItems(userId?: string): RecentItem[] {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(`${RECENT_KEY_PREFIX}${userId}`);
    if (!raw) return [];
    const items: RecentItem[] = JSON.parse(raw);
    return items.sort((a, b) => b.accessedAt - a.accessedAt).slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function saveRecentItem(userId: string | undefined, entry: CommandPaletteEntry): void {
  if (!userId) return;
  try {
    const items = getRecentItems(userId);
    const filtered = items.filter((item) => item.id !== entry.id);
    filtered.unshift({
      id: entry.id,
      label: entry.label,
      type: entry.type,
      path: entry.path,
      accessedAt: Date.now(),
    });
    localStorage.setItem(
      `${RECENT_KEY_PREFIX}${userId}`,
      JSON.stringify(filtered.slice(0, MAX_RECENT))
    );
  } catch {
    // Silently ignore storage errors
  }
}

// ============================================================================
// Component
// ============================================================================

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
}

export function CommandPalette({ isOpen, onClose, userId }: CommandPaletteProps) {
  const { t } = useTranslation(["common", "modules"]);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Build page entries from module registry
  const pageEntries = useMemo<CommandPaletteEntry[]>(() => {
    const modules = moduleRegistry.getAll();
    return modules
      .filter((mod) => mod.nav?.path)
      .map((mod) => {
        const label = t(`modules:${mod.id}.title`, { defaultValue: mod.id });
        return {
          id: `page:${mod.id}`,
          label,
          type: "page" as const,
          path: mod.nav!.path,
          icon: mod.icon,
          description: mod.meta.description,
        };
      });
  }, [t]);

  // Build recent entries
  const recentEntries = useMemo<CommandPaletteEntry[]>(() => {
    return getRecentItems(userId).map((item) => ({
      id: `recent:${item.id}`,
      label: item.label,
      type: "recent" as const,
      path: item.path,
      icon: Clock,
    }));
  }, [userId, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter entries based on query
  const filteredEntries = useMemo(() => {
    const q = query.toLowerCase().trim();

    if (!q) {
      // Show recent items first, then all pages
      const sections: { title: string; entries: CommandPaletteEntry[] }[] = [];
      if (recentEntries.length > 0) {
        sections.push({ title: t("common:command-palette.recent", "Recent"), entries: recentEntries.slice(0, 5) });
      }
      sections.push({ title: t("common:command-palette.pages", "Pages"), entries: pageEntries });
      return sections;
    }

    const matchingPages = pageEntries.filter(
      (entry) =>
        entry.label.toLowerCase().includes(q) ||
        entry.path.toLowerCase().includes(q) ||
        (entry.description && entry.description.toLowerCase().includes(q))
    );

    if (matchingPages.length === 0) {
      return [];
    }

    return [{ title: t("common:command-palette.pages", "Pages"), entries: matchingPages }];
  }, [query, pageEntries, recentEntries, t]);

  // Flat list of all entries for keyboard navigation
  const flatEntries = useMemo(() => {
    return filteredEntries.flatMap((section) => section.entries);
  }, [filteredEntries]);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      // Focus the input after a brief delay to let the dialog mount
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  // Reset selected index when entries change
  useEffect(() => {
    setSelectedIndex(0);
  }, [flatEntries.length]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const selected = listRef.current.querySelector('[data-selected="true"]');
    if (selected) {
      selected.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (entry: CommandPaletteEntry) => {
      saveRecentItem(userId, entry);
      onClose();
      navigate(entry.path);
    },
    [userId, onClose, navigate]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, flatEntries.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (flatEntries[selectedIndex]) {
            handleSelect(flatEntries[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    },
    [flatEntries, selectedIndex, handleSelect, onClose]
  );

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[20vh] z-50 w-full max-w-lg translate-x-[-50%] rounded-xl border bg-white shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=open]:slide-in-from-left-1/2"
          onKeyDown={handleKeyDown}
        >
          {/* Accessible title for screen readers */}
          <DialogPrimitive.Title className="sr-only">
            {t("common:command-palette.title", "Command Palette")}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {t("common:command-palette.description", "Search and navigate to any page")}
          </DialogPrimitive.Description>

          {/* Search Input */}
          <div className="flex items-center border-b px-3">
            <Search className="h-4 w-4 shrink-0 text-gray-400" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("common:command-palette.placeholder", "Search pages...")}
              className="flex h-12 w-full bg-transparent px-3 py-3 text-sm outline-none placeholder:text-gray-400"
              autoComplete="off"
              spellCheck={false}
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="shrink-0 rounded p-1 text-gray-400 hover:text-gray-600"
                aria-label={t("common:clear", "Clear")}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[300px] overflow-y-auto p-2" role="listbox">
            {filteredEntries.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500">
                {t("common:command-palette.no-results", "No results found")}
              </div>
            ) : (
              filteredEntries.map((section) => (
                <div key={section.title} className="mb-1">
                  <div className="px-2 py-1.5 text-xs font-medium text-gray-500">
                    {section.title}
                  </div>
                  {section.entries.map((entry) => {
                    const globalIndex = flatEntries.indexOf(entry);
                    const isSelected = globalIndex === selectedIndex;
                    const Icon = entry.icon || FileText;

                    return (
                      <button
                        key={entry.id}
                        role="option"
                        aria-selected={isSelected}
                        data-selected={isSelected}
                        onClick={() => handleSelect(entry)}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-left transition-colors",
                          isSelected
                            ? "bg-primary-50 text-primary-700"
                            : "text-gray-700 hover:bg-gray-50"
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0 opacity-60" />
                        <div className="flex-1 truncate">
                          <span>{entry.label}</span>
                        </div>
                        {isSelected && (
                          <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-40" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-gray-400">
            <div className="flex items-center gap-2">
              <kbd className="rounded border bg-gray-50 px-1.5 py-0.5 font-mono text-[10px]">↑↓</kbd>
              <span>{t("common:command-palette.navigate", "Navigate")}</span>
              <kbd className="rounded border bg-gray-50 px-1.5 py-0.5 font-mono text-[10px]">↵</kbd>
              <span>{t("common:command-palette.select", "Select")}</span>
              <kbd className="rounded border bg-gray-50 px-1.5 py-0.5 font-mono text-[10px]">esc</kbd>
              <span>{t("common:command-palette.close", "Close")}</span>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
